/**
 * A física do anel de fita: cada fio do anel dobrado é uma corrente de
 * partículas (integração de Verlet) presa nas duas pontas — onde a fita sai do
 * apoio e o mosquetão. A gravidade puxa as partículas e as restrições de
 * distância só impedem o fio de ESTICAR além do comprimento de repouso (fita
 * não resiste a compressão: ela dobra e pende).
 *
 *  - Sem carga, o comprimento de repouso é o do anel: sobra fita e ela pende.
 *  - Com carga, a tensão de centenas de quilos faz a flecha (`peso·L²/8T`)
 *    sumir: o comprimento de repouso cai até a distância entre as pontas e a
 *    tensão puxa cada partícula para a reta entre elas. A troca é suave (o
 *    repouso e a tensão caminham até o alvo), então ao pôr ou tirar o peso a
 *    fita estica ou afrouxa e balança um pouco.
 *
 * TypeScript puro com Float64Array: sem React e sem Three.js.
 */

export type V3 = readonly [number, number, number]

export const GRAVIDADE_FITA: V3 = [0, -9.80665, 0]
/** Partículas por fio (incluindo as duas pontas). */
export const PARTICULAS_POR_FIO = 14
const AMORTECIMENTO = 0.985
const ITERACOES = 16
/** Passo fixo da integração, em segundos. */
export const PASSO_S = 1 / 120
/** Rapidez com que o comprimento de repouso alcança o alvo, em 1/s: esticar é rápido, afrouxar é mais lento. */
const RAPIDEZ_AO_ESTICAR = 9
const RAPIDEZ_AO_AFROUXAR = 3.5

export interface FioDeFita {
  readonly n: number
  readonly atual: Float64Array
  readonly anterior: Float64Array
  /** Comprimento de repouso do fio inteiro, em metros. */
  repousoM: number
  /** De 0 (sem carga) a 1 (sob carga): quanto a tensão alinha o fio. */
  tensao: number
}

const distancia = (a: V3, b: V3) => Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])

/**
 * A forma de repouso aproximada de um fio de `comprimento` entre `a` e `b`:
 * uma parábola que pende na direção da gravidade (perpendicular à corda),
 * com a flecha `s` que dá o comprimento de arco `L ≈ d + 8s²/(3d)`.
 */
export function formaPendente(a: V3, b: V3, comprimento: number, n = PARTICULAS_POR_FIO, gravidade: V3 = GRAVIDADE_FITA): V3[] {
  const d = distancia(a, b)
  const sobra = Math.max(0, comprimento - d)
  const flecha = d > 1e-9 ? Math.sqrt((3 * d * sobra) / 8) : sobra / 2
  // a direção em que o fio pende: a gravidade sem a componente ao longo da corda
  const g = Math.hypot(gravidade[0], gravidade[1], gravidade[2]) || 1
  const gu: V3 = [gravidade[0] / g, gravidade[1] / g, gravidade[2] / g]
  const t: V3 = d > 1e-9 ? [(b[0] - a[0]) / d, (b[1] - a[1]) / d, (b[2] - a[2]) / d] : [0, 0, 0]
  const k = gu[0] * t[0] + gu[1] * t[1] + gu[2] * t[2]
  let p: V3 = [gu[0] - k * t[0], gu[1] - k * t[1], gu[2] - k * t[2]]
  const cp = Math.hypot(p[0], p[1], p[2])
  // fio na vertical: pende de lado nenhum (só encolhe), sem flecha
  p = cp > 1e-6 ? [p[0] / cp, p[1] / cp, p[2] / cp] : [0, 0, 0]
  return Array.from({ length: n }, (_, i) => {
    const u = i / (n - 1)
    const f = 4 * flecha * u * (1 - u)
    return [a[0] + (b[0] - a[0]) * u + p[0] * f, a[1] + (b[1] - a[1]) * u + p[1] * f, a[2] + (b[2] - a[2]) * u + p[2] * f] as const
  })
}

export function criarFio(a: V3, b: V3, comprimento: number, n = PARTICULAS_POR_FIO): FioDeFita {
  const forma = formaPendente(a, b, comprimento, n)
  const atual = new Float64Array(n * 3)
  forma.forEach((q, i) => atual.set(q, i * 3))
  return { n, atual, anterior: atual.slice(), repousoM: comprimento, tensao: 0 }
}

/** Quanto da distância até a reta a tensão total corrige a cada iteração. */
const ALINHAMENTO_SOB_CARGA = 0.35

/**
 * Avança o fio `dt` segundos (em passos fixos), com as pontas em `a` e `b`, o
 * comprimento de repouso caminhando até `alvoM` e a tensão até 1 com carga (0 sem).
 */
export function avancarFio(fio: FioDeFita, a: V3, b: V3, alvoM: number, carregada: boolean, dt: number, gravidade: V3 = GRAVIDADE_FITA): void {
  const passos = Math.max(1, Math.min(8, Math.round(dt / PASSO_S)))
  const h = dt / passos
  const { n, atual: x, anterior: x0 } = fio
  for (let s = 0; s < passos; s += 1) {
    const rapidez = alvoM < fio.repousoM ? RAPIDEZ_AO_ESTICAR : RAPIDEZ_AO_AFROUXAR
    fio.repousoM += (alvoM - fio.repousoM) * (1 - Math.exp(-rapidez * h))
    fio.tensao += ((carregada ? 1 : 0) - fio.tensao) * (1 - Math.exp(-(carregada ? RAPIDEZ_AO_ESTICAR : RAPIDEZ_AO_AFROUXAR) * h))
    // Verlet: posição nova = atual + (atual − anterior)·amortecimento + g·h²
    for (let i = 1; i < n - 1; i += 1) {
      for (let c = 0; c < 3; c += 1) {
        const k = i * 3 + c
        const v = (x[k]! - x0[k]!) * AMORTECIMENTO
        x0[k] = x[k]!
        x[k] = x[k]! + v + gravidade[c]! * h * h
      }
    }
    const seg = fio.repousoM / (n - 1)
    for (let it = 0; it < ITERACOES; it += 1) {
      x.set(a, 0)
      x.set(b, (n - 1) * 3)
      for (let i = 0; i < n - 1; i += 1) {
        const p = i * 3
        const q = p + 3
        const dx = x[q]! - x[p]!
        const dy = x[q + 1]! - x[p + 1]!
        const dz = x[q + 2]! - x[p + 2]!
        const d = Math.hypot(dx, dy, dz)
        // a fita só resiste a esticar
        if (d <= seg || d < 1e-12) continue
        const corr = (d - seg) / d
        const fixoP = i === 0
        const fixoQ = i + 1 === n - 1
        const wp = fixoP ? 0 : fixoQ ? 1 : 0.5
        const wq = fixoQ ? 0 : fixoP ? 1 : 0.5
        x[p] = x[p]! + dx * corr * wp
        x[p + 1] = x[p + 1]! + dy * corr * wp
        x[p + 2] = x[p + 2]! + dz * corr * wp
        x[q] = x[q]! - dx * corr * wq
        x[q + 1] = x[q + 1]! - dy * corr * wq
        x[q + 2] = x[q + 2]! - dz * corr * wq
      }
      // a tensão da carga puxa o fio para a reta entre as pontas
      const k = fio.tensao * ALINHAMENTO_SOB_CARGA
      if (k > 1e-4) {
        for (let i = 1; i < n - 1; i += 1) {
          const u = i / (n - 1)
          for (let c = 0; c < 3; c += 1) {
            const reta = a[c]! + (b[c]! - a[c]!) * u
            x[i * 3 + c] = x[i * 3 + c]! + (reta - x[i * 3 + c]!) * k
          }
        }
      }
    }
    x.set(a, 0)
    x.set(b, (n - 1) * 3)
  }
}

/** O alvo do comprimento de repouso: reto com carga, o comprimento do anel sem ela. */
export const alvoDoFio = (a: V3, b: V3, comprimentoSemCargaM: number, carregada: boolean): number => {
  const d = distancia(a, b)
  return carregada ? d : Math.max(d, comprimentoSemCargaM)
}

/** A flecha do fio: a maior distância de uma partícula à reta entre as pontas. */
export function flechaDoFio(fio: FioDeFita): number {
  const { n, atual: x } = fio
  const a: V3 = [x[0]!, x[1]!, x[2]!]
  const b: V3 = [x[(n - 1) * 3]!, x[(n - 1) * 3 + 1]!, x[(n - 1) * 3 + 2]!]
  const d = distancia(a, b)
  if (d < 1e-12) return 0
  const t: V3 = [(b[0] - a[0]) / d, (b[1] - a[1]) / d, (b[2] - a[2]) / d]
  let maior = 0
  for (let i = 1; i < n - 1; i += 1) {
    const r: V3 = [x[i * 3]! - a[0], x[i * 3 + 1]! - a[1], x[i * 3 + 2]! - a[2]]
    const k = r[0] * t[0] + r[1] * t[1] + r[2] * t[2]
    maior = Math.max(maior, Math.hypot(r[0] - k * t[0], r[1] - k * t[1], r[2] - k * t[2]))
  }
  return maior
}
